
import { mcpClient } from "../../lib/mcp";

let cachedToolsDescription: string | null = null;
let cachedOpenAITools: any[] | null = null;
let cachedRawTools: any[] | null = null; // Raw MCP tool schemas for executeAction
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/** Shared connection helper — avoids duplicate connect+wait calls */
const ensureConnected = async () => {
    if (mcpClient.isConnected) return;
    await mcpClient.connect();
    await mcpClient.waitForInitialization();
};

/**
 * Generates a compressed string description of all available MCP tools.
 * Cached for 5 minutes to avoid redundant MCP round-trips on every message.
 */
export const getToolsDescription = async (): Promise<string> => {
  try {
    const now = Date.now();
    if (cachedToolsDescription && (now - cacheTimestamp) < CACHE_TTL) {
      return cachedToolsDescription;
    }

    await ensureConnected();
    const tools = await mcpClient.listTools();
    cachedRawTools = tools; // Cache raw tools for executeAction schema lookups

    if (!tools || tools.length === 0) {
      return "No relevant tools found for this query.";
    }

    console.log("[MCP] Refreshed tools cache:", tools.map(t => t.name).join(", "));

    cachedToolsDescription = tools
      .map((t: any) => {
        const props = t.inputSchema?.properties || {};
        const compressedProps: Record<string, string> = {};
        Object.keys(props).forEach((k) => {
          if (k !== "user_id" && k !== "calendar_id") {
             compressedProps[k] = props[k].type || "string";
          }
        });
        
        return `${t.name}: ${t.description.split('.')[0]} (${JSON.stringify(compressedProps)})`;
      })
      .join("\n");

    cacheTimestamp = now;
    return cachedToolsDescription;
  } catch (e) {
    console.warn("Failed to get tools description:", e);
    return cachedToolsDescription || "No tools available (error).";
  }
};

/**
 * Converts MCP tool schemas into OpenAI-compatible `tools` format.
 * This enables native function calling — the model is structurally constrained
 * to output valid tool_calls instead of free-form JSON.
 * 
 * Output format: [{ type: "function", function: { name, description, parameters } }]
 */
export const getToolsAsOpenAIFunctions = async (): Promise<any[]> => {
  try {
    const now = Date.now();
    if (cachedOpenAITools && (now - cacheTimestamp) < CACHE_TTL) {
      return cachedOpenAITools;
    }

    await ensureConnected();
    const tools = await mcpClient.listTools();
    cachedRawTools = tools; // Cache raw tools

    if (!tools || tools.length === 0) {
      return [];
    }

    console.log("[MCP] Building OpenAI tools from MCP schemas:", tools.map(t => t.name).join(", "));

    cachedOpenAITools = tools.map((t: any) => {
      const schema = t.inputSchema || {};
      const properties = { ...(schema.properties || {}) };
      const required = [...(schema.required || [])];

      // Remove user_id from the schema — it's injected automatically at execution time
      delete properties.user_id;
      const filteredRequired = required.filter(r => r !== "user_id");

      return {
        type: "function" as const,
        function: {
          name: t.name,
          description: (t.description || "").substring(0, 1024), // Cap description length (1024 for pipeline tool docs)
          parameters: {
            type: "object",
            properties,
            required: filteredRequired.length > 0 ? filteredRequired : undefined,
          },
        },
      };
    });

    cacheTimestamp = now;
    return cachedOpenAITools;
  } catch (e) {
    console.warn("Failed to build OpenAI tools:", e);
    return cachedOpenAITools || [];
  }
};

/** Force-refresh tools cache (call after server restart) */
export const invalidateToolsCache = () => {
  cachedToolsDescription = null;
  cachedOpenAITools = null;
  cachedRawTools = null;
  cacheTimestamp = 0;
};

/** Get cached raw tool schema for executeAction auto-mapping (avoids extra MCP round-trip) */
export const getCachedToolSchema = (toolName: string): any | null => {
  if (!cachedRawTools) return null;
  return cachedRawTools.find((t: any) => t.name === toolName)?.inputSchema || null;
};
